import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { AltModal as Modal } from 'ui-kit';
import { AutoTagsModalProps, MultipleChoiceAutoTag } from './types';
import CheckboxVariant from './CheckboxVariant';
import ModalFooterNew from 'components/common/Modal/ModalFooterNew';
import YesNoVariant from './YesNoVariant';
import { ATSJobEditTestIds } from 'data-testids/ATS';
import { QuestionType } from 'utils/constants';
import MultipleChoiceVariant from './MultipleChoiceVariant';
import { UserCreatedOption } from 'types/types';

const AutoTagsModal: React.FC<AutoTagsModalProps> = ({
  onClose,
  questionType,
  show,
  existingAutoTags,
  fetchedTags,
  onSave,
  multipleChoiceOptions,
}) => {
  const isYesNoQuestion = questionType === QuestionType.yes_no;
  const isStatementCheckbox = questionType === QuestionType.statementCheckbox;
  const isMultipleChoiceQuestion = questionType === QuestionType.multiple_choice;

  const [yesOptionValue, setYesOptionValue] = useState(() => {
    if (!existingAutoTags) {
      return null;
    }

    const value = existingAutoTags.find((tag) => tag.option === 'Yes');
    if (!value) return null;
    return value.tag_id;
  });

  const [noOptionValue, setNoOptionValue] = useState(() => {
    if (!existingAutoTags) {
      return null;
    }

    const value = existingAutoTags.find((tag) => tag.option === 'No');
    if (!value) return null;
    return value.tag_id;
  });

  const [agreeOptionValue, setAgreeOptionValue] = useState(() => {
    if (!existingAutoTags) {
      return null;
    }

    const value = existingAutoTags.find((tag) => tag.option === 'Agree');
    if (!value) return null;
    return value.tag_id;
  });

  const [disagreeOptionValue, setDisagreeOptionValue] = useState(() => {
    if (!existingAutoTags) {
      return null;
    }

    const value = existingAutoTags.find((tag) => tag.option === 'Disagree');
    if (!value) return null;
    return value.tag_id;
  });

  const [multipleChoiceSelections, setMultipleChoiceSelections] = useState<
    MultipleChoiceAutoTag[] | []
  >(() => {
    if (!existingAutoTags) {
      return [];
    }

    const autotagsToReturn = [];

    existingAutoTags.forEach((tag) => {
      if (
        tag.option !== 'Agree' &&
        tag.option !== 'Disagree' &&
        tag.option !== 'Yes' &&
        tag.option !== 'No'
      ) {
        autotagsToReturn.push(tag);
      }
    });

    return autotagsToReturn;
  });

  const handleSetYesOptionValue = (id) => setYesOptionValue(id);
  const handleSetNoOptionValue = (id) => setNoOptionValue(id);
  const handleSetAgreeOptionValue = (id) => setAgreeOptionValue(id);
  const handleSetDisagreeOptionValue = (id) => setDisagreeOptionValue(id);

  const handleSave = () => {
    const yesNumber = yesOptionValue === null ? null : Number(yesOptionValue);
    const noNumber = noOptionValue === null ? null : Number(noOptionValue);
    const agreeNumber = agreeOptionValue === null ? null : Number(agreeOptionValue);
    const disagreeNumber = disagreeOptionValue === null ? null : Number(disagreeOptionValue);
    const multipleChoiceOptions =
      multipleChoiceSelections?.length === 0 ? null : multipleChoiceSelections;

    onSave({
      Yes: yesNumber,
      No: noNumber,
      Agree: agreeNumber,
      Disagree: disagreeNumber,
      MultipleChoice: multipleChoiceOptions,
    });

    onClose();
  };

  const handleMultipleChoiceSave = (tagId, option: UserCreatedOption) => {
    const newTagId = Number(tagId);
    setMultipleChoiceSelections((previousState) => {
      const filteredState = previousState.filter((tag) => tag.option !== option);

      if (newTagId && tagId !== '') {
        return [...filteredState, { tag_id: newTagId, option }];
      }

      return filteredState;
    });
  };

  return (
    <Modal.AltModal isOpen={show} onClose={onClose}>
      <Modal.Title>Automatic Tags</Modal.Title>
      <Body>
        <AssignmentText>
          Assign tags based on a candidate&apos;s response to this question.
        </AssignmentText>

        {isYesNoQuestion && (
          <YesNoVariant
            fetchedTags={fetchedTags}
            handleSetNoOptionValue={handleSetNoOptionValue}
            handleSetYesOptionValue={handleSetYesOptionValue}
            yesOptionValue={yesOptionValue}
            noOptionValue={noOptionValue}
          />
        )}
        {isStatementCheckbox && (
          <CheckboxVariant
            fetchedTags={fetchedTags}
            handleSetAgreeOptionValue={handleSetAgreeOptionValue}
            handleSetDisagreeOptionValue={handleSetDisagreeOptionValue}
            agreeOptionValue={agreeOptionValue}
            disagreeOptionValue={disagreeOptionValue}
          />
        )}
        {isMultipleChoiceQuestion && (
          <MultipleChoiceVariant
            multipleChoiceOptions={multipleChoiceOptions}
            fetchedTags={fetchedTags}
            handleMultipleChoiceSave={handleMultipleChoiceSave}
            selectedValues={multipleChoiceSelections}
          />
        )}

        <Subtext>
          Changes will not be applied to candidates who&apos;ve already submitted applications.
        </Subtext>

        <Subtext>
          You can create a new tag from the{' '}
          <SettingsLink to="/district/settings">settings page.</SettingsLink>
        </Subtext>
      </Body>
      <Modal.Actions>
        <ModalFooterNew
          cancel={onClose}
          save={handleSave}
          saveButtonLabel="Save"
          saveDisabled={false}
          saveButtonTestId={ATSJobEditTestIds.AUTO_TAGS_SAVE_BUTTON}
        />
      </Modal.Actions>
    </Modal.AltModal>
  );
};

export default AutoTagsModal;

const AssignmentText = styled('div')({
  paddingBottom: '24px',
});

const Body = styled(Modal.Body)({
  fontSize: '16px',
  padding: '27px 27px',
});

const SettingsLink = styled(Link)({
  color: ' #00A37D',
  paddingTop: '24px',
});

const Subtext = styled('div')({
  color: '#666',
  fontSize: '14px',
  fontStyle: 'italic',
  fontWeight: 400,
  paddingBottom: '4px',
});
